/**
 * BootScreen — o que a pessoa vê enquanto o app abre, e o que ela vê quando
 * abrir dá errado.
 *
 * Substitui o spinner cru (`PageLoader`) nas esperas de SESSÃO — as longas, que
 * dependem de rede. O `PageLoader` continua servindo o `Suspense` de chunk, que
 * é uma espera de milissegundos e não merece uma tela de marca piscando.
 *
 * Três estados, um componente:
 *
 *  1. **Carregando** — só wordmark + barra + microcopy, sem ícone acima dela.
 *     17/09/2026: as duas primeiras tentativas de "marca de espera" (estante
 *     de 3 lombadas, depois um "L" num anel) tentavam ser um SEGUNDO
 *     elemento de marca — e as duas leram mal rodando de verdade: a estante
 *     "estranha" na animação, o anel redundante em cima do "Livra" já
 *     escrito embaixo. A correção não é a 3ª forma, é parar de duplicar a
 *     marca: o wordmark JÁ é o centro, e a barra já é a atividade. Sem
 *     spinner girando: um disco ao lado da marca só acrescenta ansiedade.
 *  2. **Falhou** — código da etapa (`BOOT_AUTH_TIMEOUT`…) e "Tentar de novo".
 *     O código fica VISÍVEL de propósito: é o que a pessoa manda por print, e
 *     é o nosso único canal de diagnóstico sem Safari Web Inspector.
 *  3. **Painel de log** — toque longo de 5s em qualquer um dos dois abre o
 *     trace das etapas. Deliberadamente escondido: não é UI de produto, é
 *     ferramenta de suporte, e vale iOS e Android igualmente.
 *
 * ⚠️ **Os `<button>` daqui são raw de propósito** (3 warnings de DS-drift,
 * assumidos). Esta é a tela que aparece quando o app NÃO conseguiu abrir:
 * quanto menos ela importar, menor a chance de o próprio diagnóstico falhar
 * junto com o que está sendo diagnosticado. Pelo mesmo motivo ela não usa
 * ícone, portal, contexto nem hook de tema — só tokens de cor.
 */

import { useEffect, useState } from "react";
// O painel e o gesto moram em `DiagnosticLog` desde 20/08: a tela de PRO
// tambem pendura, e uma segunda copia divergiria da primeira.
import { DiagnosticLogPanel } from "./DiagnosticLog";
import { useLongPress } from "./diagnosticLogHooks";
import { useBootProgress, useBootStage } from "../../lib/boot/bootProgress";
import { LivraLogo } from "../brand/LivraLogo";

/**
 * Marca no centro — a assinatura "Fita em L" (ícone + livra), igual ao ícone
 * do app desde 13/10/2026. É o ÚNICO elemento fixo da tela, por isso grande.
 * Em curva: não espera webfont, aparece no primeiro frame.
 */
function Wordmark() {
  return <LivraLogo height={48} className="text-ink" />;
}

/**
 * Depois de quanto tempo na MESMA etapa a tela admite que está demorando.
 * Abaixo do teto de 10 s de cada etapa (`BOOT_TIMEOUT_MS`): a frase existe
 * para a janela entre "normal" e "falhou", que era muda.
 */
const SLOW_HINT_MS = 6_000;

/**
 * Microcopy da espera — muda com a ETAPA em voo (ver `describeBootStage`) e
 * entra em fade a cada troca. Passados 6 s na mesma etapa, ganha uma segunda
 * linha honesta: nada de "quase lá" — só que está demorando mais que o normal.
 */
function StageCopy({ message }: { message?: string }) {
  const stage = useBootStage();
  const text = message ?? stage;
  // Guarda PARA QUAL frase o aviso de lentidão valeu: trocar de etapa zera o
  // aviso por derivação (`slowFor !== text`), sem setState síncrono no efeito.
  const [slowFor, setSlowFor] = useState<string | null>(null);
  const slow = slowFor === text;

  useEffect(() => {
    const t = setTimeout(() => setSlowFor(text), SLOW_HINT_MS);
    return () => clearTimeout(t);
  }, [text]);

  return (
    <div className="flex min-h-[44px] flex-col items-center gap-1">
      <p key={text} className="boot-copy text-body text-ink opacity-80">
        {text}
      </p>
      {slow && (
        <p className="boot-copy text-caption text-text-muted">
          Está demorando mais que o normal…
        </p>
      )}
      <style>{`
        .boot-copy { animation: boot-copy-in 320ms ease-out both; }
        @keyframes boot-copy-in { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .boot-copy { animation: none; } }
      `}</style>
    </div>
  );
}

/**
 * Barra de progresso do boot.
 *
 * ── Duas camadas com significados diferentes, e a distinção é o desenho ─────
 *  · **Preenchimento** = verdade. Só avança quando uma etapa do boot assenta
 *    (ver `bootProgress.ts`). É por isso que ele pode ficar parado: parado
 *    significa "a etapa atual ainda não terminou", que é informação real.
 *  · **Brilho que percorre** = atividade. Existe exatamente para o caso acima:
 *    sem ele, uma etapa lenta desenha uma barra imóvel, que lê como travada —
 *    o problema que esta tela inteira existe para resolver.
 *
 * Fingir progresso no preenchimento resolveria o visual e destruiria o sinal:
 * a barra chegaria perto do fim e pararia lá, e o "quase pronto" viraria a
 * mentira que a pessoa aprende a ignorar.
 *
 * ⚠️ `role="progressbar"` SEM `aria-valuenow` é deliberado: para leitor de tela
 * isso é "indeterminado", que é a verdade — a fração é boa o bastante para
 * desenhar e não é precisa o bastante para anunciar "43 por cento".
 *
 * ── O trilho é `primary/20` por MEDIÇÃO, não por gosto ──────────────────────
 * Há um trade-off direto: quanto mais visível o trilho, menor o contraste do
 * preenchimento CONTRA ele — e é o preenchimento que carrega a informação, então
 * é ele quem tem de respeitar o piso de 3:1 de elemento gráfico. Medido em 6
 * temas, claros e escuros:
 *
 *   `line-soft` → trilho quase invisível (1,07 no Linho): a barra vira um traço
 *                 solto e para de comunicar "quanto falta"
 *   `line`      → reprova o preenchimento no Matcha (2,90)
 *   `ink/18`    → reprova no Matcha (2,93) e no Midnight (2,49)
 *   `primary/20`→ trilho no melhor valor alcançável (1,26–1,66) com o
 *                 preenchimento em **3,15+ em todos**
 *
 * De quebra é o que faz sentido no desenho: o trilho é a mesma tinta do
 * preenchimento, mais fraca — lê como "o resto do caminho", e não como uma
 * linha cinza que não pertence a nada.
 *
 * 17/09/2026 (redesenho "estante rica"): trilho mais fino (3px, era 4) e
 * mais curto (120px, era 168) — ao lado da estante maior e do wordmark a
 * 52px, a barra larga competia por atenção. O preenchimento ganhou um
 * degradê primary→secondary (as duas cores já usadas nas lombadas) em vez de
 * cor chapada, só um acabamento — a lógica de largura/teste continua a mesma.
 */
function BootProgressBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div
      role="progressbar"
      aria-label="Carregando"
      className="relative h-[3px] w-[120px] overflow-hidden rounded-full bg-primary/15"
    >
      <div
        className="boot-bar-fill h-full rounded-full"
        style={{ width: `${pct}%`, background: "linear-gradient(90deg, var(--brand-primary, #bc4b2f), var(--brand-secondary, #a86f1d))" }}
      />
      <div className="boot-bar-sheen pointer-events-none absolute inset-0" aria-hidden="true" />
      <style>{`
        .boot-bar-fill { transition: width 520ms cubic-bezier(.22,.61,.36,1); }
        .boot-bar-sheen {
          background: linear-gradient(90deg, transparent 0%, color-mix(in srgb, var(--brand-primary) 45%, transparent) 50%, transparent 100%);
          animation: boot-sheen 1500ms ease-in-out infinite;
        }
        @keyframes boot-sheen {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .boot-bar-fill { transition: none; }
          /* Degrada para ESTÁTICO, nunca para invisível: o preenchimento é a
             informação, e ele continua lá. Só o movimento sai. */
          .boot-bar-sheen { animation: none; opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export interface BootScreenProps {
  /** Código da etapa que estourou. `null` = ainda carregando. */
  failureCode?: string | null;
  /** "Tentar de novo". Só aparece quando há falha. */
  onRetry?: () => void;
  /** Microcopy do estado de carregamento. */
  message?: string;
}

export function BootScreen({
  failureCode = null,
  onRetry,
  message,
}: BootScreenProps) {
  const [showLog, setShowLog] = useState(false);
  const longPress = useLongPress(() => setShowLog(true));
  const progress = useBootProgress();

  if (showLog) return <DiagnosticLogPanel onClose={() => setShowLog(false)} />;

  return (
    <div
      {...longPress}
      className="relative flex min-h-dvh w-full flex-col items-center justify-center gap-4 overflow-hidden bg-background px-8 text-center"
    >
      {/* Fundo em duas camadas — halo quente atrás da marca (primary) +
          brilho baixo no rodapé (secondary), ambos em tokens de tema. O halo
          único a 7% da versão original lia como vazio; duas camadas dão o
          "fundo cheio" sem sair do papel do tema nem arriscar contraste em
          tema escuro (ambas ficam bem abaixo do texto, que está em
          `text-ink` sólido). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(65% 46% at 50% 36%, color-mix(in srgb, var(--brand-primary) 10%, transparent), transparent 72%), " +
            "radial-gradient(55% 38% at 50% 96%, color-mix(in srgb, var(--brand-secondary) 8%, transparent), transparent 75%)",
        }}
      />
      <div className="relative">
        <Wordmark />
      </div>

      {failureCode ? (
        <>
          <p className="max-w-[280px] text-body text-ink">
            Não conseguimos abrir o app agora. Verifique sua conexão e tente de novo.
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-1 min-h-11 rounded-control bg-primary px-6 text-body font-semibold text-on-accent"
            >
              Tentar de novo
            </button>
          )}
          {/*
            Visível de propósito — é o que vai no print que a pessoa manda.

            ⚠️ **Sem `opacity`.** A primeira versão usava `opacity-70` para o
            código parecer discreto, e a medição renderizada reprovou: 4,17:1 no
            Matcha, abaixo do 4,5 que AA exige a 11px. Este código é a
            informação FUNCIONAL da tela — se ele não for legível o suficiente
            para ser transcrito de um print, a tela inteira perde o propósito.
            A discrição fica por conta do tamanho e da fonte mono, não da tinta.
          */}
          {/*
            `max-w` + `break-words`: o `ErrorBoundary` passa a MENSAGEM do erro
            aqui em dev, e mensagem de exceção não tem limite de comprimento —
            sem isto ela estoura a largura da tela.
          */}
          <p className="mt-1 max-w-[320px] font-mono text-[11px] tracking-wide break-words text-ink">
            {failureCode}
          </p>
        </>
      ) : (
        <div className="relative flex flex-col items-center gap-3">
          <BootProgressBar value={progress} />
          <StageCopy message={message} />
        </div>
      )}
    </div>
  );
}
